import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * Header actions component that manages cart notifications.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} liveRegion - The live region for cart announcements.
 *
 * @extends {Component<Refs>}
 */
class HeaderActions extends Component {
  requiredRefs = ['liveRegion'];

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
  }

  /**
   * Handles cart update events and announces the new count to screen readers.
   * @param {Event} event
   */
  #onCartUpdate = (event) => {
    const linesEvent = /** @type {import('@shopify/events').CartLinesUpdateEvent} */ (event);

    linesEvent.promise
      ?.then((result) => {
        const cart = /** @type {{ totalQuantity?: number } | null} */ (/** @type {any} */ (result)?.cart);
        const detail = /** @type {any} */ (result)?.detail;

        const cartCount =
          typeof cart?.totalQuantity === 'number'
            ? cart.totalQuantity
            : /** @type {{ item_count?: number } | undefined} */ (detail?.resource)?.item_count;

        if (cartCount === undefined) return;
        if (this.#isCartCountAnnouncedByDrawer(linesEvent, detail)) return;

        this.refs.liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[header-actions] cart update promise rejected:', error);
      });
  };

  /**
   * Returns true when the cart drawer will announce the count so this component can stay silent.
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   * @param {{ didError?: boolean } | undefined} detail
   * @returns {boolean}
   */
  #isCartCountAnnouncedByDrawer(event, detail) {
    const cartDrawer = /** @type {HTMLElement | null} */ (document.querySelector('cart-drawer-component'));
    if (!cartDrawer) return false;

    const dialog = cartDrawer.querySelector('dialog');
    if (dialog?.open) return true;

    // Successful add that auto-opens the drawer — drawer live region handles the announcement.
    if (event.action === 'add' && detail?.didError !== true && cartDrawer.hasAttribute('auto-open')) {
      return true;
    }

    return false;
  }
}

if (!customElements.get('header-actions')) {
  customElements.define('header-actions', HeaderActions);
}
